import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {StaticImageData} from 'next/image';
import DisplayImage from '@/components/Ui-components/display-image';
import parse from 'html-react-parser';
import {colors, spacing, fontSize, fontWeight, borderRadius, shadows, typography} from '@/styles/tokens';
import Eyebrow from '@/components/Common/Eyebrow';

export interface EndToEndServiceItem {
    icon: string | StaticImageData;
    title: string;
    subtitle: string;
    description: string;
    tags: string[];
}

export interface EndToEndServicesProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    services: EndToEndServiceItem[];
    chevronUp: StaticImageData;
    chevronDown: StaticImageData;
}

const accordionListSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[16]
} as const;

const iconBgSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.lg,
    width: spacing[40],
    height: spacing[40],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
} as const;

const tagSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.full,
    px: spacing[12],
    py: spacing[8],
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0
} as const;

const tagTextSx = {
    ...typography.caption,
    color: colors.primary.link,
    whiteSpace: 'nowrap' as const
} as const;

const headerWrapSx = {
    display: 'flex',
    gap: spacing[20],
    alignItems: 'center'
} as const;

const iconInnerSx = {
    width: 20,
    height: 20,
    position: 'relative' as const
} as const;

const serviceTextColSx = {
    flex: '1 0 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[4]
} as const;

const serviceTitleSx = {
    fontSize: fontSize[18],
    color: colors.text.primary
} as const;

const serviceSubtitleSx = {
    fontSize: fontSize[16],
    fontWeight: fontWeight.regular,
    color: colors.text.primary
} as const;

const chevronWrapSx = {
    pt: spacing[8],
    flexShrink: 0,
    width: 24,
    height: 24,
    position: 'relative' as const
} as const;

const expandedSectionSx = {
    borderTop: `1px solid ${colors.neutral.borderLight}`,
    pt: spacing[12],
    display: 'flex',
    flexDirection: 'column' as const
} as const;

const descriptionSx = {
    ...typography.body,
    color: colors.text.primary
} as const;

const tagsWrapSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing[8]
} as const;

const EndToEndServices = ({eyebrow, heading, subheading, services, chevronUp, chevronDown}: EndToEndServicesProps) => {
    const [openIdx, setOpenIdx] = useState<number>(0);

    return (
        <MUIContainer>
            <Box textAlign="center" maxWidth={1024} margin="0 auto" mb={5}>
                {eyebrow && <Eyebrow text={eyebrow} />}
                <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}}>
                    {heading}
                </Typography>
                <Typography component="p" className="font-regular-text remove-underline">
                    {parse(subheading)}
                </Typography>
            </Box>

            <Box sx={accordionListSx}>
                {services.map((service, idx) => {
                    const isOpen = openIdx === idx;
                    return (
                        <Box
                            key={service.title}
                            onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                            sx={{
                                bgcolor: colors.background.default,
                                border: `1px solid ${colors.neutral.borderLight}`,
                                borderRadius: borderRadius.xl,
                                px: spacing[20],
                                py: '14px',
                                cursor: 'pointer',
                                boxShadow: isOpen ? shadows.lg : shadows.lg,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: spacing[16]
                            }}
                        >
                            <Box sx={headerWrapSx}>
                                <Box sx={iconBgSx}>
                                    <Box sx={iconInnerSx}>
                                        <DisplayImage src={service.icon} alt={service.title} fill />
                                    </Box>
                                </Box>

                                <Box sx={serviceTextColSx}>
                                    <Typography component="h3" className="font-headers" sx={serviceTitleSx}>
                                        {parse(service.title)}
                                    </Typography>
                                    <Typography className="font-regular-text remove-underline" sx={serviceSubtitleSx}>
                                        {service.subtitle}
                                    </Typography>
                                </Box>

                                <Box sx={chevronWrapSx}>
                                    <DisplayImage
                                        src={isOpen ? chevronUp : chevronDown}
                                        alt={isOpen ? 'Collapse section' : 'Expand section'}
                                        fill
                                    />
                                </Box>
                            </Box>

                            {isOpen && (
                                <Box sx={expandedSectionSx}>
                                    <Typography className="font-regular-text remove-underline" sx={descriptionSx}>
                                        {parse(service.description)}
                                    </Typography>
                                    {service.tags.length > 0 && (
                                        <Box sx={tagsWrapSx}>
                                            {service.tags.map((tag) => (
                                                <Box key={tag} sx={tagSx}>
                                                    <Typography sx={tagTextSx}>{tag}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </MUIContainer>
    );
};

export default memo(EndToEndServices);
